// Toolbar above the grid: title + count, search box, "New" action.
//
// Phase 2 keeps it minimal — sort selector lives in column headers,
// secondary filters (tags / favourites / treatment) come back in
// phase 3 alongside the slide-over panel.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDensity, type Density } from '../grid/useDensity.js';

interface GridToolbarProps {
  title: string;
  count: number | null;
  query: string;
  onQueryChange(q: string): void;
  newHref: string;
}

export function GridToolbar({ title, count, query, onQueryChange, newHref }: GridToolbarProps) {
  const [draft, setDraft] = useState(query);
  const [density, setDensity] = useDensity();

  useEffect(() => {
    setDraft(query);
  }, [query]);

  // Debounce the URL write so each keystroke doesn't refire the list().
  useEffect(() => {
    if (draft === query) return;
    const id = window.setTimeout(() => onQueryChange(draft), 300);
    return () => window.clearTimeout(id);
  }, [draft, query, onQueryChange]);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Title + count is desktop-only: on mobile the page name is in
          MobileTopbar and the count is just clutter for the user. */}
      <div className="hidden md:flex items-baseline gap-2">
        <h1 className="text-xl font-semibold text-strong">{title}</h1>
        {count !== null && (
          <span className="text-sm text-faint tabular-nums">
            {count} row{count === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search…"
          className="input w-full"
          aria-label={`Search ${title}`}
        />
      </div>
      {/* Density and New are desktop-only. Mobile gets a FAB rendered
          by the view itself. */}
      <div className="hidden md:flex items-center gap-2">
        <DensityToggle value={density} onChange={setDensity} />
        <Link to={newHref} className="btn-primary px-3 py-1.5 text-sm font-medium">
          New
        </Link>
      </div>
    </div>
  );
}

function DensityToggle({ value, onChange }: { value: Density; onChange(d: Density): void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(value === 'compact' ? 'comfortable' : 'compact')}
      className="btn-secondary px-2.5 py-1.5 text-xs font-medium"
      aria-label={`Switch to ${value === 'compact' ? 'comfortable' : 'compact'} rows`}
      title={`Density: ${value} (click to toggle)`}
    >
      {value === 'compact' ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="3" y1="14" x2="21" y2="14" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="3" y1="7" x2="21" y2="7" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="17" x2="21" y2="17" />
        </svg>
      )}
    </button>
  );
}
